'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import {
  EyeIcon,
  EyeSlashIcon,
  CheckIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';

interface LoginForm {
  email: string;
  password: string;
}

export default function AdminLoginClient() {
  const router = useRouter();
  const { user, loading: authLoading, signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginForm>();

  useEffect(() => {
    if (!authLoading && user) {
      router.replace('/admin-panel');
    }
  }, [user, authLoading, router]);

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Signed in successfully');
      router.push('/admin-panel');
    } catch (err: any) {
      setIsLoading(false);
      setError('root', {
        message: err?.message ?? 'Invalid credentials. Please check your email and password.',
      });
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <svg className="w-8 h-8 animate-spin text-primary" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4A8 8 0 014 12z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex w-[480px] xl:w-[560px] flex-shrink-0 gradient-hero flex-col justify-between p-10 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 30% 70%, rgba(212,160,23,0.15) 0%, transparent 60%)' }}
        />

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <AppLogo size={44} />
          <div>
            <p className="font-bold text-white text-lg tracking-tight">MOC Certification</p>
            <p className="text-blue-200 text-xs font-medium tracking-widest uppercase">
              Admin Portal
            </p>
          </div>
        </div>

        {/* Middle Content */}
        <div className="relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center mb-6">
            <ShieldCheckIcon className="w-8 h-8 text-accent" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4 leading-tight">
            Certificate Management System
          </h2>
          <p className="text-blue-200 text-base leading-relaxed mb-8">
            Securely manage ISO certificates, upload new certifications, and monitor
            the status of all issued certificates from this admin panel.
          </p>

          <ul className="flex flex-col gap-3">
            {[
              'Upload and manage ISO certificates',
              'Track certificate expiry and renewal status',
              'Enable customers to verify certificates online',
              'Full audit trail of all certificate operations',
            ].map((item, i) => (
              <li key={`feature-${i}`} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center flex-shrink-0">
                  <CheckIcon className="w-3 h-3 text-accent" />
                </div>
                <span className="text-sm text-blue-100">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom */}
        <p className="text-blue-300 text-xs relative z-10">
          © 2026 MOC Certification. Restricted access — authorized personnel only.
        </p>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex lg:hidden items-center gap-2.5 mb-8">
            <AppLogo size={36} />
            <div>
              <p className="font-bold text-primary text-base">MOC Certification</p>
              <p className="text-muted-foreground text-xs">Admin Portal</p>
            </div>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground mb-1.5">
              Sign in to Admin Panel
            </h1>
            <p className="text-muted-foreground text-sm">
              Enter your credentials to access the certificate management system.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
            {/* Root Error */}
            {errors.root && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2.5 animate-scale-in">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700">{errors.root.message}</p>
              </div>
            )}

            {/* Email */}
            <div>
              <label className="label" htmlFor="email">
                Email Address
              </label>
              <div className="relative">
                <UserCircleIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="admin@moccertification.com"
                  className={`input-field pl-10 ${errors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
                  suppressHydrationWarning
                  {...register('email', {
                    required: 'Email address is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Enter a valid email address',
                    },
                  })}
                />
              </div>
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="label" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className={`input-field pl-10 pr-10 ${errors.password ? 'border-red-400 focus:ring-red-400' : ''}`}
                  suppressHydrationWarning
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 4,
                      message: 'Password must be at least 4 characters',
                    },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  suppressHydrationWarning
                >
                  {showPassword ? (
                    <EyeSlashIcon className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <EyeIcon className="w-4 h-4 text-muted-foreground" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-xs text-red-600">{errors.password.message}</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              suppressHydrationWarning
              className="btn-primary w-full py-3 text-base justify-center mt-1"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4A8 8 0 014 12z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
}